import { useMemo, useRef, useState } from 'react';
import type { PageMessage } from './prima-nota-actions';
import type { LatestJudgement } from './prima-nota-generate';
import type { DeclarationOnlyItem, ProducedRegister } from './prima-nota-model';

/**
 * The two halves of the judgement the screen could not carry until now, and the profile.
 *
 * `treatments` is keyed by the open item's own TEXT rather than by a pattern, because that is
 * what the operator points at. The pattern is derived at send time — see `literalPattern`.
 * Keeping the text as the key also means a re-run whose items are unchanged keeps every
 * treatment already typed, which is the whole point of the classify-then-regenerate loop.
 */
export function useJudgement() {
  const [notes, setNotes] = useState('');
  const [declarations, setDeclarations] = useState<DeclarationOnlyItem[]>([]);
  const [treatments, setTreatments] = useState<Record<string, string>>({});
  const [profileNote, setProfileNote] = useState('');
  // Read at the moment the register is requested, not when the button was pressed: asking Drive
  // and nexpense first can take most of a minute, and a treatment typed meanwhile belongs in it.
  const latestJudgement = useRef<LatestJudgement>({ notes, declarations, treatments });
  latestJudgement.current = { notes, declarations, treatments };
  // A profile that exists is changed by storing a new revision. Before this there was no way to
  // do it from the screen: the form showed only while no profile existed.
  const [revisingProfile, setRevisingProfile] = useState(false);

  const editDeclaration = (index: number, patch: Partial<DeclarationOnlyItem>) => {
    setDeclarations((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const addDeclaration = () =>
    setDeclarations([
      ...declarations,
      { date: '', counterparty: '', ref: '', amount: '', treatment: '' },
    ]);

  const removeDeclaration = (index: number) =>
    setDeclarations(declarations.filter((_, i) => i !== index));

  const setTreatment = (text: string, treatment: string) =>
    setTreatments((current) => ({ ...current, [text]: treatment }));

  // The judgement is a reading of ONE month. Carrying last month's declared items or treatments
  // into this one is the exact mistake `reviewed_for` exists to catch on the server; the screen
  // must not manufacture it locally.
  const clearForNewPeriod = () => {
    setDeclarations([]);
    setTreatments({});
  };

  return {
    notes,
    setNotes,
    declarations,
    editDeclaration,
    addDeclaration,
    removeDeclaration,
    treatments,
    setTreatment,
    profileNote,
    setProfileNote,
    revisingProfile,
    setRevisingProfile,
    latestJudgement,
    clearForNewPeriod,
  };
}

/** What the actions on the month leave on the screen: which one runs, what it said, what it made. */
export function useActionSlots() {
  // One action at a time: the slot holds the action in flight, and every control that starts one
  // is locked while it is taken — else a second action's `finally` would free it (and unlock the
  // month) while the first is still running.
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<PageMessage | null>(null);
  const [produced, setProduced] = useState<ProducedRegister | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  return {
    busy,
    setBusy,
    message,
    setMessage,
    produced,
    setProduced,
    withdrawing,
    setWithdrawing,
    withdrawReason,
    setWithdrawReason,
  };
}

/** Day formats for the page: the viewer's own zone, and the books' calendar (Rome). */
export function useDayFormats(language: string | undefined) {
  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(language ?? 'it', { dateStyle: 'short' }),
    [language],
  );
  /**
   * Days on the books' calendar, in Rome, whatever the viewer's zone: a sweep run just after
   * midnight belongs to the new day there, and the server judges lateness on that same day.
   */
  const booksDay = useMemo(
    () =>
      new Intl.DateTimeFormat(language ?? 'it', {
        dateStyle: 'short',
        timeZone: 'Europe/Rome',
      }),
    [language],
  );
  return { dateFormat, booksDay };
}
